import { jsx, jsxs } from 'react/jsx-runtime';
import { colors } from '@flex-design-system/fx-colors';
import { styled } from '@flex-design-system/stitches-react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { Root } from '../patched-avatar/Avatar.mjs';
import { avatar, avatarBackground, avatarIconSize, avatarFontWeight, avatarFontSize, avatarRoot, avatarSize } from './avatar.vanilla.mjs';
import { AvatarFallback } from './components/fallback/Fallback.mjs';
import { AvatarImage } from './components/image/Image.mjs';
import { AvatarOutline } from './components/outline/Outline.mjs';
import { AVATAR_SIZE_RECORD } from './constants/size.mjs';
import { AvatarContextProvider } from './context.mjs';
import { isAvatarPresetSize } from './utils/cast.mjs';
import { getAvatarBackground } from './utils/color.mjs';
import { getAvatarFontStyles } from './utils/font.mjs';
import { getAvatarIconSize } from './utils/icon.mjs';

const AvatarImpl = forwardRef(
  ({
    className: classNameFromProps,
    color,
    colorScheme,
    size = "default",
    style: styleFromProps,
    ...props
  }, forwardedRef) => {
    const [avatarRootElement, setAvatarRootElement] = useState(null);
    const [avatarImageLoadStatus, setAvatarImageLoadStatus] = useState("idle");
    const restrictedAvatarSize = isAvatarPresetSize(size) ? AVATAR_SIZE_RECORD[size] : size;
    const className = classNames(avatar, classNameFromProps);
    const { fontSize, fontWeight } = getAvatarFontStyles(size);
    const iconSize = getAvatarIconSize(size);
    const style = Object.assign(
      {},
      assignInlineVars({
        [avatarFontSize]: `${fontSize}px`,
        [avatarFontWeight]: `${fontWeight}`,
        [avatarIconSize]: `${iconSize}px`,
        [avatarBackground]: avatarImageLoadStatus === "loading" ? colors.grayLightest : color !== void 0 ? getAvatarBackground({ color, colorScheme }) : null
      }),
      styleFromProps
    );
    return /* @__PURE__ */ jsx(
      AvatarContextProvider,
      {
        avatarRootElement,
        avatarImageLoadStatus,
        onAvatarImageLoadStatusChange: setAvatarImageLoadStatus,
        colorScheme,
        size: restrictedAvatarSize,
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            "data-scope": "avatar",
            "data-part": "root",
            className: avatarRoot,
            style: assignInlineVars({
              [avatarSize]: `${restrictedAvatarSize}px`
            }),
            ref: setAvatarRootElement,
            children: [
              /* @__PURE__ */ jsx("div", { "data-scope": "avatar", "data-part": "outline-portal" }),
              /* @__PURE__ */ jsx(
                StyledAvatarRoot,
                {
                  "data-scope": "avatar",
                  "data-part": "avatar",
                  className,
                  style,
                  ref: forwardedRef,
                  ...props
                }
              )
            ]
          }
        )
      }
    );
  }
);
const StyledAvatarRoot = styled(Root);
const Avatar = Object.assign({}, styled(AvatarImpl), {
  Image: styled(AvatarImage),
  Fallback: styled(AvatarFallback),
  Outline: styled(AvatarOutline)
});
Avatar.displayName = "Avatar";

export { Avatar };
//# sourceMappingURL=Avatar.mjs.map
